// resizeImageToBlob — client-side JPEG re-encode + downscale.
//
// Shared helper so uploadPinPhoto / uploadMessagePhoto / uploadFragmentPhotos
// don't each carry their own near-identical resize routine. Future tweaks to
// MAX_DIMENSION_PX / JPEG_QUALITY land in one place.

const DEFAULT_MAX_DIMENSION_PX = 1024;
const DEFAULT_JPEG_QUALITY = 0.78;

export interface ResizeOptions {
  maxDim?: number;
  quality?: number;
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
}

export async function resizeImageToBlob(
  file: File,
  opts: ResizeOptions = {},
): Promise<Blob> {
  const maxDim = opts.maxDim ?? DEFAULT_MAX_DIMENSION_PX;
  const quality = opts.quality ?? DEFAULT_JPEG_QUALITY;

  const img = await loadImage(file);
  const ratio = Math.min(maxDim / img.width, maxDim / img.height, 1);
  const w = Math.round(img.width * ratio);
  const h = Math.round(img.height * ratio);

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas 2d context unavailable");
  ctx.drawImage(img, 0, 0, w, h);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("canvas.toBlob returned null"));
      },
      "image/jpeg",
      quality,
    );
  });
}
